// src/pages/settings/ChartsAccountsPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MoreVertical, Plus, Search, Pencil, Hash, Layers, Tag, ChartNoAxesColumn } from "lucide-react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SettingsMenu } from "@/components/Holizontal/SettingsMenu";

import { ResponsiveDialogContent } from "./dialog/ResponsiveDialogContent";
import { ChartAccountForm, Option, ChartFormState } from "./dialog/ChartAccountForm";

type ChartRow = {
  id: number;
  code: string;
  name: string;
  type?: string | null; // Assets/Revenue/etc label or nullable
  CATEGORY?: string; // category label
  category_id?: number | null; // optional if your API returns it
};

type CursorMeta = {
  per_page?: number;
  next_cursor: string | null;
  prev_cursor?: string | null;
  has_more: boolean;
};

type CursorResponse<T> = {
  data: T[];
  meta: CursorMeta;
  anchor_id?: number | null;
};

type ApiListResponse<T> = { data: T[] };

function clsx(...s: Array<string | false | undefined | null>) {
  return s.filter(Boolean).join(" ");
}

function useDebouncedValue<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function useInfiniteSentinel(opts: { enabled: boolean; onLoadMore: () => void; root?: Element | null }) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!opts.enabled) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) opts.onLoadMore();
      },
      { root: opts.root ?? null, rootMargin: "420px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [opts.enabled, opts.onLoadMore, opts.root]);

  return sentinelRef;
}

function SkeletonRow({ rtl }: { rtl: boolean }) {
  return (
    <tr className="border-b">
      <td className="py-4">
        <div className="h-4 w-20 rounded bg-muted animate-pulse" />
      </td>
      <td className="py-4">
        <div className="h-4 w-56 rounded bg-muted animate-pulse" />
      </td>
      <td className="py-4">
        <div className="h-4 w-32 rounded bg-muted animate-pulse" />
      </td>
      <td className="py-4">
        <div className="h-4 w-32 rounded bg-muted animate-pulse" />
      </td>
      <td className={clsx("py-4", rtl ? "text-left" : "text-right")}>
        <div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" />
      </td>
    </tr>
  );
}

/** ✅ MOBILE card item */
function ChartCard({ r, rtl, onEdit }: { r: ChartRow; rtl: boolean; onEdit: () => void }) {
  return (
    <Card className="shadow-card rounded-2xl overflow-hidden">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold truncate">{r.name}</div>

            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 text-xs">
                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Code:</span>
                <span className="font-semibold">{r.code}</span>
              </span>

              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 text-xs">
                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Category:</span>
                <span className="font-semibold">{r.CATEGORY ?? "—"}</span>
              </span>

              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 text-xs">
                <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Type:</span>
                <span className="font-semibold">{r.type ?? "—"}</span>
              </span>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-xl shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}

type DialogMode = "create" | "edit";

export default function ChartsAccountsPage() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const rtl = dir === "rtl";
  const mainEl = useMemo(() => document.querySelector("main"), []);

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);
  const [perPage, setPerPage] = useState(25);

  // ✅ Single dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>("create");

  // ✅ Category search (depends on form.type)
  const [categorySearch, setCategorySearch] = useState("");
  const categorySearchDebounced = useDebouncedValue(categorySearch, 300);

  const emptyForm: ChartFormState = {
    code: "",
    name: "",
    type: "",
    category_id: "",
  };

  const [form, setForm] = useState<(ChartFormState & { id: number | null })>({ id: null, ...emptyForm });

  // ✅ Categories list depends on selected type
  const categoriesQ = useQuery({
    queryKey: ["chart_categories", form.type, categorySearchDebounced],
    enabled: !!form.type,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("type", String(form.type));
      if (categorySearchDebounced.trim()) params.set("q", categorySearchDebounced.trim());
      const res = await apiFetch<ApiListResponse<Option>>(`/settings/charts/categories?${params.toString()}`);
      return res.data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const chartsInf = useInfiniteQuery({
    queryKey: ["charts_cursor", qDebounced, perPage],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("per_page", String(perPage));
      if (pageParam) params.set("cursor", String(pageParam));
      if (qDebounced?.trim()) params.set("q", qDebounced.trim());
      return apiFetch<CursorResponse<ChartRow>>(`/settings/charts?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage?.meta?.has_more ? lastPage.meta.next_cursor : null),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });

  const pages = chartsInf.data?.pages ?? [];
  const rows = useMemo(() => pages.flatMap((p) => p.data || []), [pages]);
  const lastMeta = pages.length ? pages[pages.length - 1]?.meta : undefined;

  const loadingFirst = chartsInf.isLoading;
  const loadingMore = chartsInf.isFetchingNextPage;

  const sentinelRef = useInfiniteSentinel({
    enabled: !!chartsInf.hasNextPage && !chartsInf.isFetchingNextPage && !chartsInf.isLoading,
    onLoadMore: () => {
      if (chartsInf.hasNextPage && !chartsInf.isFetchingNextPage) chartsInf.fetchNextPage();
    },
    root: (mainEl as Element) ?? null,
  });

  const validate = () => {
    if (!form.code.trim()) return t("common.code_required", "Code is required");
    if (!form.name.trim()) return t("common.name_required", "Name is required");
    if (!form.type) return t("common.type_required", "Type is required");
    if (!form.category_id) return t("common.category_required", "Category is required");
    return null;
  };

  const buildPayload = (): ChartFormState => ({
    code: form.code.trim(),
    name: form.name.trim(),
    type: form.type,
    category_id: form.category_id,
  });

  // ✅ Create
  const createMut = useMutation({
    mutationFn: async (payload: ChartFormState) => {
      return apiFetch<ChartRow>("/settings/charts", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: async () => {
      toast.success(t("common.save", "Saved"));
      setDialogOpen(false);
      setDialogMode("create");
      setForm({ id: null, ...emptyForm });
      setCategorySearch("");
      await qc.invalidateQueries({ queryKey: ["charts_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || t("common.failed", "Failed")),
  });

  // ✅ Edit
  const editMut = useMutation({
    mutationFn: async (payload: { id: number; data: ChartFormState }) => {
      return apiFetch(`/settings/charts/${payload.id}`, {
        method: "PUT",
        body: JSON.stringify(payload.data),
      });
    },
    onSuccess: async () => {
      toast.success(t("common.save", "Saved"));
      setDialogOpen(false);
      setDialogMode("create");
      setForm({ id: null, ...emptyForm });
      setCategorySearch("");
      await qc.invalidateQueries({ queryKey: ["charts_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || t("common.failed", "Failed")),
  });

  const saving = createMut.isPending || editMut.isPending;

  const openCreate = () => {
    setDialogMode("create");
    setForm({ id: null, ...emptyForm });
    setCategorySearch("");
    setDialogOpen(true);
  };

  const openEdit = (r: ChartRow) => {
    setDialogMode("edit");
    setCategorySearch("");
    setForm({
      id: r.id,
      code: r.code ?? "",
      name: r.name ?? "",
      type: (r.type as any) ?? "",
      category_id: r.category_id ? String(r.category_id) : "",
    });
    setDialogOpen(true);
  };

  const onSubmitDialog = () => {
    const err = validate();
    if (err) return toast.error(err);

    const payload = buildPayload();

    if (dialogMode === "create") {
      createMut.mutate(payload);
      return;
    }

    if (!form.id) return toast.error("Missing id");
    editMut.mutate({ id: form.id, data: payload });
  };

  const dialogTitle =
    dialogMode === "create" ? t("charts.create", "Create Account") : t("charts.edit", "Edit Account");

  return (
    <div dir={dir} className="space-y-6">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <ChartNoAxesColumn className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">{t("charts.title", "Charts of Accounts")}</h1>
              <p className="text-xs text-muted-foreground">Infinite scroll</p>
            </div>
          </div>

          <Button
            onClick={openCreate}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-[0_10px_30px_rgba(124,58,237,0.18)]"
            disabled={loadingFirst}
          >
            <Plus className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
            {t("common.create", "Create")}
          </Button>
        </div>
      </header>

      {chartsInf.isError ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-red-600">
            {(chartsInf.error as any)?.message || "Failed to load"}
          </CardContent>
        </Card>
      ) : null}

      <SettingsMenu />

      <Card className="shadow-card overflow-hidden">
        <CardHeader className="border-b border-border/60">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-lg">{t("charts.list", "All Accounts")}</CardTitle>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className={clsx("absolute top-2.5 h-4 w-4 text-muted-foreground", rtl ? "right-3" : "left-3")} />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("common.search", "Search...")}
                  className={clsx("w-full sm:w-72 rounded-xl", rtl ? "pr-10" : "pl-10")}
                />
              </div>

              <select
                className="h-10 rounded-xl border border-border bg-background px-3 text-sm"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {t("common.rows_per_page", "Rows per page")}: {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Desktop table */}
          <div className="hidden md:block w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
                <tr className="text-left">
                  <th className="p-3 whitespace-nowrap">{t("common.code", "Code")}</th>
                  <th className="p-3 whitespace-nowrap">{t("common.name", "Name")}</th>
                  <th className="p-3 whitespace-nowrap">{t("common.category", "Category")}</th>
                  <th className="p-3 whitespace-nowrap">{t("common.type", "Type")}</th>
                  <th className={clsx("p-3 whitespace-nowrap", rtl ? "text-left" : "text-right")}>
                    {t("common.actions", "Actions")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {loadingFirst ? (
                  <>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <SkeletonRow key={`sk-${i}`} rtl={rtl} />
                    ))}
                  </>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-10 text-center text-muted-foreground">
                      {t("common.no_data", "No data available")}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-muted/20">
                      <td className="p-3">{r.code}</td>
                      <td className="p-3 font-semibold">{r.name}</td>
                      <td className="p-3">{r.CATEGORY ?? "—"}</td>
                      <td className="p-3">{r.type ?? "—"}</td>

                      <td className={clsx("p-3", rtl ? "text-left" : "text-right")}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="rounded-xl">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
                            <DropdownMenuItem onClick={() => openEdit(r)}>
                              <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                              {t("common.edit", "Edit")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}

                {loadingMore ? (
                  <>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <SkeletonRow key={`more-${i}`} rtl={rtl} />
                    ))}
                  </>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-3">
            {loadingFirst ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border bg-background p-4">
                  <div className="h-4 w-48 rounded bg-muted animate-pulse" />
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <div className="h-6 w-28 rounded-full bg-muted animate-pulse" />
                    <div className="h-6 w-32 rounded-full bg-muted animate-pulse" />
                    <div className="h-6 w-24 rounded-full bg-muted animate-pulse" />
                  </div>
                </div>
              ))
            ) : rows.length === 0 ? (
              <div className="rounded-xl border border-border p-6 text-center text-sm text-muted-foreground">
                {t("common.no_data", "No data available")}
              </div>
            ) : (
              rows.map((r) => <ChartCard key={r.id} r={r} rtl={rtl} onEdit={() => openEdit(r)} />)
            )}

            <Button
              variant="outline"
              className="w-full rounded-xl"
              onClick={() => chartsInf.fetchNextPage()}
              disabled={!chartsInf.hasNextPage || chartsInf.isFetchingNextPage}
            >
              {chartsInf.isFetchingNextPage
                ? t("common.loading", "Loading...")
                : chartsInf.hasNextPage
                ? t("common.load_more", "Load more")
                : t("common.no_more", "No more")}
            </Button>
          </div>

          <div ref={sentinelRef} />

          {/* Footer (desktop) */}
          <div className="hidden md:flex items-center justify-between gap-3 border-t border-border/60 p-4">
            <div className="text-sm text-muted-foreground">
              {t("common.showing", "Showing")}{" "}
              <span className="font-medium text-foreground">{rows.length}</span>{" "}
              {lastMeta?.has_more ? "(more available)" : "(end)"}
            </div>

            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => chartsInf.fetchNextPage()}
              disabled={!chartsInf.hasNextPage || chartsInf.isFetchingNextPage}
            >
              {chartsInf.isFetchingNextPage
                ? t("common.loading", "Loading...")
                : chartsInf.hasNextPage
                ? t("common.next", "Next")
                : t("common.no_more", "No more")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ✅ FINAL: Responsive create/edit dialog with dependent categories */}
      <ResponsiveDialogContent
        title={dialogTitle}
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setDialogMode("create");
            setCategorySearch("");
            setForm({ id: null, ...emptyForm });
          }
        }}
        onSubmit={onSubmitDialog}
        saving={saving}
        submitLabel={dialogMode === "create" ? t("common.create", "Create") : t("common.update", "Update")}
      >
        <ChartAccountForm
          form={form}
          setForm={(updater) =>
            setForm((prev) => {
              const next = typeof updater === "function" ? (updater as any)(prev) : updater;
              return { ...prev, ...next, id: prev.id }; // keep id stable
            })
          }
          categories={categoriesQ.data ?? []}
          categorySearch={categorySearch}
          setCategorySearch={setCategorySearch}
          categoriesLoading={categoriesQ.isLoading}
        />
      </ResponsiveDialogContent>
    </div>
  );
}